import React, { useState, useContext, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Upload, Link, X } from 'lucide-react'
import { DashboardContext } from '@/context/DashboardContext'
import { RemoteConfigContext } from '@/context/RemoteConfigContext'
import { useCustomFetch } from '@/hooks/useCustomFetch'
import toast from 'react-hot-toast'
import Uppy from '@uppy/core'
import { Dashboard } from '@uppy/react'
import '@uppy/core/dist/style.css'
import '@uppy/dashboard/dist/style.css'

interface Document {
  id: string
  name: string
  url: string
  type: string
}

interface CreateProductDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
  simplified?: boolean
  title?: string
  fitContent?: boolean
  product?: any // Product to edit (optional - if provided, dialog will be in edit mode)
}

export function CreateProductDialog({
  open,
  onOpenChange,
  onSuccess,
  simplified = false,
  title = 'Add New Product',
  fitContent = false,
  product = null,
}: CreateProductDialogProps) {
  const { config, isLoading: remoteConfigLoading } = useContext(RemoteConfigContext)
  const { products, setProducts } = useContext(DashboardContext)
  const [selectedImage, setSelectedImage] = useState<string | null>(null)
  const [productName, setProductName] = useState('')
  const [platform, setPlatform] = useState('')
  const [status, setStatus] = useState('ideation')
  const [tagline, setTagline] = useState('')
  const [description, setDescription] = useState('')
  const [uniqueSellingProposition, setUniqueSellingProposition] = useState('')
  const [documents, setDocuments] = useState<Document[]>([])
  const [links, setLinks] = useState<any[]>([])
  const [newLink, setNewLink] = useState('')
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const customFetch = useCustomFetch()

  const remoteSources = []
  if (!remoteConfigLoading) {
    if (config.googleDrive) remoteSources.push('GoogleDrive')
    if (config.oneDrive) remoteSources.push('OneDrive')
    if (config.zoom) remoteSources.push('Zoom')
    if (config.dropbox) remoteSources.push('Dropbox')
    if (config.box) remoteSources.push('Box')
  }

  const resetForm = () => {
    setProductName('')
    setPlatform('')
    setStatus('ideation')
    setTagline('')
    setDescription('')
    setUniqueSellingProposition('')
    setSelectedImage(null)
    setDocuments([])
    setLinks([])
    setNewLink('')
    setIsUploading(false)
    setUploadProgress(0)
  }

  const handleUpdateProduct = async () => {
    if (!productName.trim()) return toast.error('Product name is required')
    if (!tagline.trim()) return toast.error('Tagline is required')
    if (!simplified && !platform.trim()) return toast.error('Platform is required')
    if (!simplified && !status.trim()) return toast.error('Status is required')
    if (!description.trim()) return toast.error('Description is required')
    if (!simplified && !uniqueSellingProposition.trim()) return toast.error('Unique Selling Proposition is required')

    setIsSubmitting(true)
    const updatedProduct = {
      id: product.id,
      name: productName,
      tagline,
      description,
      uniqueSellingProposition: simplified ? 'To be defined' : uniqueSellingProposition,
      status: simplified ? 'Ideation' : status === 'active' ? 'Live' : status.charAt(0).toUpperCase() + status.slice(1),
      platform: simplified
        ? 'Web'
        : platform === 'web'
          ? 'Web'
          : platform === 'ios'
            ? 'iOS'
            : platform === 'android'
              ? 'Android'
              : platform === 'macos'
                ? 'Mac OS'
                : platform === 'windows'
                  ? 'Windows'
                  : platform === 'linux'
                    ? 'Linux'
                    : platform === 'multiplatform'
                      ? 'Multi-Platform'
                      : 'Web',
      ...(selectedImage ? { logo: selectedImage } : {}),
      links: simplified ? [] : links,
      documents: simplified ? [] : documents,
    }

    try {
      await customFetch('/updateProduct', 'PUT', { ...updatedProduct })

      // Invalidate cache for products
      customFetch.invalidateCache('/getProducts', 'GET')

      // Update products in context
      setProducts((prevProducts) => prevProducts.map((p) => (p.id === product.id ? { ...p, ...updatedProduct } : p)))

      resetForm()
      toast.success('Product updated successfully!')

      if (onSuccess) {
        await onSuccess()
      }

      // Only close the dialog after onSuccess completes
      onOpenChange(false)
    } catch (error) {
      toast.error('Failed to update product')
      console.error('Error updating product:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCreateProduct = async () => {
    if (!productName.trim()) return toast.error('Product name is required')
    if (!tagline.trim()) return toast.error('Tagline is required')
    if (!simplified && !platform.trim()) return toast.error('Platform is required')
    if (!simplified && !status.trim()) return toast.error('Status is required')
    if (!description.trim()) return toast.error('Description is required')
    if (!simplified && !uniqueSellingProposition.trim()) return toast.error('Unique Selling Proposition is required')

    setIsSubmitting(true)
    const newProduct = {
      name: productName,
      tagline,
      description,
      uniqueSellingProposition: simplified ? 'To be defined' : uniqueSellingProposition,
      status: simplified ? 'Ideation' : status === 'active' ? 'Live' : status.charAt(0).toUpperCase() + status.slice(1),
      platform: simplified
        ? 'Web'
        : platform === 'web'
          ? 'Web'
          : platform === 'ios'
            ? 'iOS'
            : platform === 'android'
              ? 'Android'
              : platform === 'macos'
                ? 'Mac OS'
                : platform === 'windows'
                  ? 'Windows'
                  : platform === 'linux'
                    ? 'Linux'
                    : platform === 'multiplatform'
                      ? 'Multi-Platform'
                      : 'Web',
      ...(selectedImage ? { logo: selectedImage } : {}),
      order: products.length + 1,
      links: simplified ? [] : links,
      documents: simplified ? [] : documents,
    }

    try {
      // create product in db get the just created product id
      const response = await customFetch('/createProduct', 'POST', { ...newProduct })

      setProducts((prevProducts) => [...prevProducts, { ...newProduct, id: response.id, projectsData: [] }])

      resetForm()
      toast.success('Product created successfully!')

      if (onSuccess) {
        await onSuccess()
      }

      // Only close the dialog after onSuccess completes
      onOpenChange(false)
    } catch (error) {
      toast.error('Failed to create product')
      console.error('Error creating product:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (e) => {
        setSelectedImage(e.target?.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleRemoveImage = () => {
    setSelectedImage(null)
  }

  const handleAddLink = () => {
    if (newLink.trim() && !links.includes(newLink.trim())) {
      setLinks([...links, newLink.trim()])
      setNewLink('')
    }
  }

  const handleRemoveLink = (linkToRemove: string) => {
    setLinks(links.filter((link) => link !== linkToRemove))
  }

  // Reset form when dialog closes or populate when editing
  useEffect(() => {
    if (!open) {
      resetForm()
    } else if (product) {
      // Populate form with existing product data for editing
      setProductName(product.name || '')
      setTagline(product.tagline || '')
      setDescription(product.description || '')
      setUniqueSellingProposition(product.uniqueSellingProposition || '')
      setSelectedImage(product.logo || null)
      setLinks(product.links || [])
      setDocuments(product.documents || [])

      // Map platform values
      const platformMap = {
        Web: 'web',
        iOS: 'ios',
        Android: 'android',
        'Mac OS': 'macos',
        Windows: 'windows',
        Linux: 'linux',
        'Multi-Platform': 'multiplatform',
      }
      setPlatform(platformMap[product.platform] || 'web')

      // Map status values
      const statusMap = {
        Ideation: 'ideation',
        Development: 'development',
        Testing: 'testing',
        Live: 'active',
        Maintenance: 'maintenance',
        Discontinued: 'discontinued',
      }
      setStatus(statusMap[product.status] || 'ideation')
    }
  }, [open, product])

  return (
    <Dialog
      open={open}
      onOpenChange={(newOpen) => {
        // Prevent closing the dialog if an operation is in progress
        if (isSubmitting && !newOpen) {
          return
        }
        onOpenChange(newOpen)
      }}
    >
      <DialogContent
        className={`max-w-4xl rounded-2xl p-0 flex flex-col ${fitContent ? 'max-h-[90vh]' : 'h-[80vh]'}`}
        onPointerDownOutside={(e) => {
          // Prevent closing by clicking outside when submitting
          if (isSubmitting) {
            e.preventDefault()
          }
        }}
        onEscapeKeyDown={(e) => {
          // Prevent closing by pressing Escape when submitting
          if (isSubmitting) {
            e.preventDefault()
          }
        }}
      >
        <DialogHeader className="px-6 py-4 border-b">
          <DialogTitle className="text-xl font-semibold">
            {title}
            {isSubmitting && <span className="ml-2 text-sm text-gray-500 font-normal">(Please wait...)</span>}
          </DialogTitle>
        </DialogHeader>

        <div className={fitContent ? 'px-6 py-4' : 'flex-1 overflow-y-auto px-6 py-4'}>
          <div className="grid gap-6">
            {/* Logo & Product details */}
            <div className="grid grid-cols-[120px_1fr] gap-6">
              <div className="space-y-2">
                <label className="text-sm font-medium">Logo</label>
                <div className="h-[120px] w-[120px] border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center relative">
                  {selectedImage ? (
                    <>
                      <img src={selectedImage} alt="Product Logo" className="w-full h-full object-cover rounded-lg" />
                      <button
                        onClick={handleRemoveImage}
                        className="absolute top-2 right-2 p-1 bg-white rounded-full shadow-sm hover:bg-gray-100"
                      >
                        <X size={16} className="text-gray-500" />
                      </button>
                    </>
                  ) : (
                    <div className="text-center">
                      <Upload size={24} className="mx-auto text-gray-400" />
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      />
                      <p className="text-xs text-gray-500 mt-1">Click to upload</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                <div className={simplified ? 'space-y-2' : 'grid grid-cols-2 gap-4'}>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Product Name *</label>
                    <input
                      type="text"
                      value={productName}
                      onChange={(e) => setProductName(e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg"
                      placeholder="Enter product name"
                    />
                  </div>
                  {!simplified && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Platform *</label>
                      <Select value={platform} onValueChange={setPlatform}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select platform" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="web">Web</SelectItem>
                          <SelectItem value="ios">iOS</SelectItem>
                          <SelectItem value="android">Android</SelectItem>
                          <SelectItem value="macos">Mac OS</SelectItem>
                          <SelectItem value="windows">Windows</SelectItem>
                          <SelectItem value="linux">Linux</SelectItem>
                          <SelectItem value="multiplatform">Multi-Platform</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                {!simplified && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Status *</label>
                      <Select value={status} onValueChange={setStatus}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ideation">Ideation</SelectItem>
                          <SelectItem value="development">Development</SelectItem>
                          <SelectItem value="testing">Testing</SelectItem>
                          <SelectItem value="active">Live</SelectItem>
                          <SelectItem value="maintenance">Maintenance</SelectItem>
                          <SelectItem value="discontinued">Discontinued</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div></div>
                  </div>
                )}

                <div className="space-y-2">
                  <label className="text-sm font-medium">Tagline *</label>
                  <input
                    type="text"
                    value={tagline}
                    onChange={(e) => setTagline(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg"
                    placeholder="Brief description of your product"
                  />
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Description *</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg h-24 resize-none"
                placeholder="Detailed description of your product"
              />
            </div>

            {/* Unique Selling Proposition */}
            {!simplified && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Unique Selling Proposition *</label>
                <textarea
                  value={uniqueSellingProposition}
                  onChange={(e) => setUniqueSellingProposition(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg h-24 resize-none"
                  placeholder="What makes your product unique?"
                />
              </div>
            )}

            {/* Links */}
            {!simplified && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Links</label>
                  <div className="flex gap-2">
                    <input
                      type="url"
                      value={newLink}
                      onChange={(e) => setNewLink(e.target.value)}
                      className="flex-1 p-3 border border-gray-300 rounded-lg"
                      placeholder="Add a link (website, documentation, etc.)"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          handleAddLink()
                        }
                      }}
                    />
                    <Button type="button" onClick={handleAddLink} variant="outline" className="px-4">
                      Add Link
                    </Button>
                  </div>

                  {links.length > 0 && (
                    <div className="h-[120px] overflow-y-auto space-y-2">
                      {links.map((link, index) => (
                        <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded-md">
                          <Link size={14} className="text-gray-500" />
                          <span className="text-sm text-gray-600 flex-1">{link}</span>
                          <button onClick={() => handleRemoveLink(link)} className="text-gray-400 hover:text-gray-600">
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className={`px-6 py-4 border-t ${fitContent ? '' : 'mt-auto'}`}>
          <Button
            variant="outline"
            onClick={() => {
              if (!isSubmitting) {
                onOpenChange(false)
              }
            }}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            className="bg-[#FF4A8E] hover:bg-[#FF4A8E]/90"
            onClick={product ? handleUpdateProduct : handleCreateProduct}
            disabled={isSubmitting}
          >
            {isSubmitting ? (product ? 'Updating...' : 'Creating...') : product ? 'Update Product' : 'Create Product'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
